import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Trash2, Save, History, Cpu, PlugZap, MapPin, Network, MonitorSmartphone, ShieldCheck, ChevronDown, Fingerprint, Activity, Warehouse, Printer, ArrowRightLeft, RotateCcw, Wrench, Archive, SlidersHorizontal, Ticket, Plus, Flag } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import { useLang } from "@/i18n";
import { fmtDate, fmtDateOnly, fmtAuto, timeAgo } from "@/lib/time";
import { loadPlacement, loadOrder, inDetail, applyOrder, fieldLabel, fieldValueText, BASE_FIELDS, tvStatusOf } from "@/lib/fieldPrefs";
import { useConfirm } from "@/components/ConfirmDialog";
import api, { apiError } from "@/lib/api";
import TicketCard from "@/components/TicketCard";
import TicketDialog from "@/components/TicketDialog";
import { sortTickets } from "@/lib/tickets";

const MONO_KEYS = new Set(["device_code", "location_id", "device_uid", "hostname", "tablet_serial", "teamviewer_id", "agent_version", "os_version", "extra.ip_address", "extra.mac_address", "extra.sdk_version", "extra.db_dat", "extra.devices_dat"]);
const COPY_KEYS = new Set(["device_uid", "hostname", "tablet_serial", "teamviewer_id", "extra.mac_address", "extra.ip_address"]);

// Logische Gruppierung der Detailfelder (zusammengehörige Daten stehen zusammen)
const DETAIL_GROUPS = [
  { id: "identity", de: "Identität", en: "Identity", icon: Fingerprint, keys: ["device_code", "location_id", "location_name", "group", "device_uid", "tablet_serial", "extra.SN-PC (Soll)", "hostname"] },
  { id: "status", de: "Status & Agent", en: "Status & Agent", icon: Activity, keys: ["agent_version", "payload_version", "first_seen", "last_seen"] },
  { id: "network", de: "Netzwerk", en: "Network", icon: Network, keys: ["extra.ip_address", "extra.mac_address", "extra.internet"] },
  { id: "teamviewer", de: "TeamViewer", en: "TeamViewer", icon: PlugZap, keys: ["teamviewer_id", "extra.teamviewer_version"] },
  { id: "hardware", de: "Hardware / System", en: "Hardware / System", icon: MonitorSmartphone, keys: ["manufacturer", "model", "extra.cpu", "extra.ram", "extra.disk_size", "extra.disk_free", "os_version"] },
  { id: "software", de: "Regula / Software", en: "Regula / Software", icon: ShieldCheck, keys: ["extra.sdk_version", "extra.db_dat", "extra.devices_dat", "extra.tsrid_app_version", "extra.scanner_type", "extra.scanner_serial", "extra.Scanner-SN (Soll)", "extra.scanner_connected"] },
];

// Extra-Felder, die in der Stationsinformationen-Kachel aufgehen (aus generischen Gruppen ausschließen)
const STATION_EXCLUDE = ["extra.Stationsname", "extra.Straße", "extra.PLZ", "extra.Ort", "extra.Adresse", "extra.Telefon", "extra.Tel intern", "extra.E-Mail Team", "extra.LocManager", "extra.MoFr", "extra.Sa", "extra.So", "extra.Agentur", "extra.KST"];

// Auswählbare Inhalte für das Geräte-Etikett
const LABEL_FIELD_DEFS = [
  { key: "qr", de: "QR-Code", en: "QR code" },
  { key: "barcode", de: "Barcode (Fallback)", en: "Barcode (fallback)" },
  { key: "tag", de: "Asset-Tag / Code", en: "Asset tag / code" },
  { key: "location", de: "Standort", en: "Location" },
  { key: "former", de: "Letzter Code", en: "Previous code" },
  { key: "serial", de: "Seriennummer", en: "Serial no." },
  { key: "tv", de: "TeamViewer-ID", en: "TeamViewer ID" },
  { key: "hostname", de: "Hostname", en: "Hostname" },
];
const LABEL_DEFAULT = { qr: true, barcode: true, tag: true, location: false, former: false, serial: false, tv: false, hostname: false };

function Card({ title, icon: Icon, children, wide = false, testid }) {
  return (
    <div data-testid={testid} className={`bg-gray-50 border border-gray-200 rounded-xl p-4 ${wide ? "md:col-span-2" : ""}`}>
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-[#c00000]/80 mb-2.5 pb-2 border-b border-gray-100">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5">{children}</div>
    </div>
  );
}

function LinkField({ label, value, href, external = false, testid }) {
  if (!value) return null;
  return (
    <div className="py-2 border-b border-gray-100">
      <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-0.5">{label}</div>
      <a data-testid={testid} href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
        className="inline-block text-sm text-[#c00000] hover:text-[#c00000] underline decoration-[#c00000]/30 hover:decoration-[#c00000] transition-colors break-all">
        {value}
      </a>
    </div>
  );
}

function Field({ label, value, mono = true, copyable = false }) {
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(value);
  };
  return (
    <div className="py-2 border-b border-gray-100">
      <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-0.5">{label}</div>
      <div className={`flex items-center gap-2 text-sm text-gray-800 ${mono ? "font-mono" : ""} break-all`}>
        <span>{value || "—"}</span>
        {copyable && value && (
          <button onClick={copy} className="text-gray-600 hover:text-[#c00000] transition-colors shrink-0">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function InternetLedField({ label, value }) {
  const v = (value || "").toString().trim().toLowerCase();
  const has = !!v;
  const online = ["online", "true", "1", "ok", "connected", "yes", "ja"].includes(v);
  return (
    <div className="py-2 border-b border-gray-100">
      <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-0.5">{label}</div>
      <div className="flex items-center gap-2 text-sm" data-testid="drawer-internet-led">
        {has ? (
          <>
            <span
              className={`inline-block w-3 h-3 rounded-full ${
                online
                  ? "bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)]"
                  : "bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.6)]"
              }`}
            />
            <span className={online ? "text-green-600" : "text-red-600"}>{online ? "online" : "offline"}</span>
          </>
        ) : (
          <span className="text-gray-600">—</span>
        )}
      </div>
    </div>
  );
}

function ScannerLedField({ label, value }) {
  const v = (value || "").toString().trim().toLowerCase();
  const has = !!v;
  const on = ["ja", "yes", "true", "1", "connected", "verbunden"].includes(v);
  return (
    <div className="py-2 border-b border-gray-100">
      <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-0.5">{label}</div>
      <div className="flex items-center gap-2 text-sm" data-testid="drawer-scanner-led">
        {has ? (
          <span title={on ? "Scanner verbunden" : "Scanner getrennt"} className={`inline-block w-3 h-3 rounded-full ${on ? "bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)]" : "bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.6)]"}`} />
        ) : (
          <span className="text-gray-600">—</span>
        )}
      </div>
    </div>
  );
}


function StatusPill({ status }) {
  const { t } = useLang();
  if (status === "online")
    return <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-500/10 text-green-600 border border-green-500/30">{t("online")}</span>;
  if (status === "offline")
    return <span className="text-xs font-mono px-2 py-0.5 rounded bg-red-500/10 text-red-600 border border-red-500/30">{t("offline")}</span>;
  if (status === "tv_reachable")
    return <span className="text-xs font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/30">{t("tv_reachable")}</span>;
  return <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-300">{t("no_agent")}</span>;
}

export default function DeviceDrawer({ device, onClose, onChanged, lang }) {
  const { t } = useLang();
  const confirm = useConfirm();
  const [history, setHistory] = useState([]);
  const [code, setCode] = useState(device.device_code || "");
  const [tv, setTv] = useState(device.teamviewer_id || "");
  const [locName, setLocName] = useState(device.location_name || "");
  const [note, setNote] = useState(device.note || "");
  const [closesOn, setClosesOn] = useState(device.closes_on || "");
  const [taskFlag, setTaskFlag] = useState(!!device.task_flag);
  const [taskNote, setTaskNote] = useState(device.task_note || "");
  const [saving, setSaving] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketUsers, setTicketUsers] = useState([]);
  const [ticketDlg, setTicketDlg] = useState(false);
  const [ticketEditing, setTicketEditing] = useState(null);
  const [labelCfgOpen, setLabelCfgOpen] = useState(false);
  const [lf, setLf] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("labelFields")); return s ? { ...LABEL_DEFAULT, ...s } : LABEL_DEFAULT; } catch { return LABEL_DEFAULT; }
  });
  const toggleLabel = (key) => setLf((p) => { const n = { ...p, [key]: !p[key] }; localStorage.setItem("labelFields", JSON.stringify(n)); return n; });
  const [labelW, setLabelW] = useState(() => { const v = parseInt(localStorage.getItem("labelWidth"), 10); return v >= 30 && v <= 80 ? v : 45; });
  const changeLabelW = (v) => { setLabelW(v); localStorage.setItem("labelWidth", String(v)); };
  const did = encodeURIComponent(device.asset_tag || device.device_code || device.device_uid || "");
  const scanUrl = `${window.location.origin}/d/${encodeURIComponent(device.asset_tag || device.device_code || device.device_uid || "")}`;
  const barcodeVal = device.asset_tag || device.device_code || device.device_uid || "";

  useEffect(() => {
    setCode(device.device_code || "");
    setTv(device.teamviewer_id || "");
    setLocName(device.location_name || "");
    setNote(device.note || "");
    setClosesOn(device.closes_on || "");
    setTaskFlag(!!device.task_flag);
    setTaskNote(device.task_note || "");
    api.get(`/devices/${did}/history`).then((r) => setHistory(r.data)).catch(() => {});
    loadTickets();
    api.get("/users").then((r) => setTicketUsers(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.device_code]);

  const loadTickets = () => {
    if (!device.location_id) { setTickets([]); return; }
    api.get("/tickets", { params: { location_id: device.location_id } })
      .then((r) => setTickets((r.data || []).filter((tk) => tk.device_code === device.device_code)))
      .catch(() => {});
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/devices/${did}`, { device_code: code, teamviewer_id: tv, location_name: locName, note, closes_on: closesOn, task_flag: taskFlag, task_note: taskFlag ? taskNote : "" });
      toast.success(lang === "de" ? "Gespeichert" : "Saved");
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged && onChanged(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const dlabel = device.device_code || device.asset_tag || device.hostname || device.device_uid || "";
  const cancelLabel = lang === "de" ? "Abbrechen" : "Cancel";
  const returnToLager = async () => {
    if (!(await confirm({
      title: lang === "de" ? "Ins Lager legen" : "Move to stock",
      message: lang === "de" ? `Wollen Sie „${dlabel}“ wirklich prüfen und ins Lager legen?` : `Really check "${dlabel}" and move it to stock?`,
      confirmLabel: lang === "de" ? "Einlagern" : "Move to stock", cancelLabel,
    }))) return;
    doAction(() => api.post(`/devices/${did}/lager`, { condition: "gebraucht" }), lang === "de" ? "Geprüft & eingelagert" : "Checked & stocked");
  };
  const markDefekt = async () => {
    if (!(await confirm({
      title: lang === "de" ? "Als defekt einstufen" : "Mark defective",
      message: lang === "de" ? `Wollen Sie „${dlabel}“ wirklich als defekt einstufen? (Reparaturchance besteht)` : `Really mark "${dlabel}" as defective? (repair still possible)`,
      confirmLabel: lang === "de" ? "Als defekt einstufen" : "Mark defective", cancelLabel,
    }))) return;
    doAction(() => api.post(`/devices/${did}/condition`, { condition: "defekt" }), lang === "de" ? "Als defekt eingestuft" : "Marked defective");
  };
  const markAusgemustert = async () => {
    if (!(await confirm({
      title: lang === "de" ? "Endgültig ausmustern" : "Retire permanently",
      message: lang === "de" ? `Wollen Sie „${dlabel}“ wirklich endgültig ausmustern? 100% defekt, nicht reparierbar.` : `Really retire "${dlabel}" permanently? 100% defective, not repairable.`,
      confirmLabel: lang === "de" ? "Ausmustern" : "Retire", cancelLabel, danger: true,
    }))) return;
    doAction(() => api.post(`/devices/${did}/condition`, { condition: "ausgemustert" }), lang === "de" ? "Ausgemustert" : "Retired");
  };
  const assignStation = async () => {
    if (!(await confirm({
      title: lang === "de" ? "Neuer Station zuweisen" : "Assign to station",
      message: lang === "de" ? `Wollen Sie „${dlabel}“ wirklich einer neuen Station zuweisen?` : `Really assign "${dlabel}" to a new station?`,
      confirmLabel: lang === "de" ? "Weiter" : "Continue", cancelLabel,
    }))) return;
    const code = window.prompt(lang === "de" ? "Neuer Stationscode (z. B. FRAT03-01):" : "New station code:", "");
    if (!code) return;
    const name = window.prompt(lang === "de" ? "Standort-Name (optional):" : "Location name (optional):", "") || "";
    doAction(() => api.post(`/devices/${did}/assign`, { device_code: code.trim(), location_name: name }), `${lang === "de" ? "Zugewiesen an" : "Assigned to"} ${code.trim()}`);
  };
  const printLabel = () => window.print();

  const remove = async () => {
    if (!(await confirm({
      title: lang === "de" ? "Gerät löschen" : "Delete device",
      message: lang === "de" ? `Wollen Sie „${dlabel}“ wirklich unwiderruflich löschen? Alle Daten und die Historie gehen verloren.` : `Really delete "${dlabel}" permanently? All data and history will be lost.`,
      confirmLabel: lang === "de" ? "Löschen" : "Delete", cancelLabel, danger: true,
    }))) return;
    try {
      await api.delete(`/devices/${did}`);
      toast.success(lang === "de" ? "Gelöscht" : "Deleted");
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const inputCls =
    "w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:border-[#c00000] focus:ring-1 focus:ring-[#c00000] outline-none";
  const tvClean = (tv || "").replace(/\s+/g, "");
  const orderedKeys = applyOrder(
    [...BASE_FIELDS, ...Object.keys(device.extra || {}).map((k) => `extra.${k}`)],
    loadOrder()
  );
  const placement = loadPlacement();
  const detailKeys = orderedKeys.filter((k) => inDetail(placement, k));

  const renderField = (k) => {
    const isDate = k === "first_seen" || k === "last_seen";
    const isDateOnly = k === "extra.devices_dat" || k === "extra.db_dat";
    let raw = fieldValueText(device, k);
    if (k === "extra.Bemerkung / Notes") return null; // wird über das editierbare Notiz-Feld verwaltet
    if (k === "extra.internet") return <InternetLedField key={k} label={fieldLabel(k, t)} value={raw} />;
    if (k === "extra.scanner_connected") return <ScannerLedField key={k} label={fieldLabel(k, t)} value={raw} />;
    // Modell: exakte Generation (z. B. Surface Pro 6) aus model_detail bevorzugen
    if (k === "model") raw = (device.extra || {}).model_detail || raw;
    let display = String(raw || "");
    if (isDate) display = fmtDate(raw);
    else if (isDateOnly && raw) display = fmtDateOnly(raw);
    else if (raw) display = String(fmtAuto(raw));
    const field = <Field label={fieldLabel(k, t)} value={display} mono={MONO_KEYS.has(k)} copyable={COPY_KEYS.has(k)} />;
    if (k === "device_uid") return <div key={k} className="sm:col-span-2">{field}</div>;
    return <div key={k}>{field}</div>;
  };

  const used = new Set([...STATION_EXCLUDE, "extra.model_detail"]);
  const sx = (k) => String((device.extra || {})[k] || "").trim();
  const st_addr = [sx("Straße"), [sx("PLZ"), sx("Ort")].filter(Boolean).join(" ")].filter(Boolean).join(", ") || sx("Adresse");
  const st = {
    name: sx("Stationsname") || device.location_name,
    addr: st_addr,
    tel: sx("Telefon"), telIntern: sx("Tel intern"),
    email: sx("E-Mail Team"), manager: sx("LocManager"),
    mofr: sx("MoFr"), sa: sx("Sa"), so: sx("So"),
    agentur: sx("Agentur"), kst: sx("KST"),
  };
  const hasStation = !!(st.name || st.addr || st.tel || st.telIntern || st.email || st.manager || st.mofr);
  const groups = DETAIL_GROUPS.map((g) => {
    const keys = g.keys.filter((k) => detailKeys.includes(k) && !used.has(k));
    keys.forEach((k) => used.add(k));
    return { id: g.id, label: lang === "de" ? g.de : g.en, icon: g.icon, keys };
  }).filter((g) => g.keys.length);
  const restKeys = detailKeys.filter((k) => !used.has(k));
  if (restKeys.length) groups.push({ id: "other", label: lang === "de" ? "Weitere" : "Other", icon: null, keys: restKeys });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="device-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[95vw] max-w-7xl max-h-[92vh] bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden scale-in">
        {/* Header */}
        <div className="shrink-0 border-b border-gray-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#c00000]/15 border border-[#c00000]/30 flex items-center justify-center">
              <Cpu className="w-4 h-4 text-[#c00000]" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-lg">{device.device_code || device.asset_tag || device.hostname}</div>
              <div className="flex items-center gap-2 text-xs text-gray-600 font-mono">
                <StatusPill status={device.status} /> · {fmtDate(device.last_seen)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {device.asset_tag && (
              <div className="text-right leading-tight" data-testid="drawer-asset-id">
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-600">AssetID</div>
                <div className="font-mono text-lg font-extrabold tracking-wide text-gray-900">{device.asset_tag}</div>
              </div>
            )}
            <button data-testid="drawer-close" onClick={onClose} className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body: 3-column dense layout */}
        <div className="print-label" aria-hidden="true" style={{ "--pl-w": `${labelW}mm` }}>
          <div className="pl-codes">
            {lf.qr && <div className="pl-qr"><QRCodeSVG value={scanUrl} size={256} level="M" /></div>}
            {lf.barcode && barcodeVal && (
              <div className="pl-bc"><Barcode value={barcodeVal} format="CODE128" displayValue={false} height={80} width={2} margin={0} background="#ffffff" lineColor="#000000" /></div>
            )}
          </div>
          {lf.tag && <div className="pl-tag">{device.asset_tag || device.device_code}</div>}
          {lf.location && device.location_name && <div className="pl-sub">{device.location_name}</div>}
          {lf.former && device.former_code && <div className="pl-sub">zuletzt: {device.former_code}</div>}
          {lf.serial && device.tablet_serial && <div className="pl-sub">S/N: {device.tablet_serial}</div>}
          {lf.tv && device.teamviewer_id && <div className="pl-sub">TV: {device.teamviewer_id}</div>}
          {lf.hostname && device.hostname && <div className="pl-sub">{device.hostname}</div>}
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Edit column */}
            <div className="lg:col-span-4 space-y-3">
              <div className="text-xs font-mono uppercase tracking-wider text-gray-600">{t("edit")}</div>
              <div>
                <label className="text-[11px] font-mono uppercase text-gray-600 mb-1 block">{t("device_code")}</label>
                <input data-testid="drawer-code-input" value={code} onChange={(e) => setCode(e.target.value)} className={`${inputCls} font-mono`} />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase text-gray-600 mb-1 block">{t("teamviewer_id")}</label>
                <input data-testid="drawer-tv-input" value={tv} onChange={(e) => setTv(e.target.value)} className={`${inputCls} font-mono`} />
                {device.tv_changed_at && device.tv_prev_id && (device.tv_prev_id || "").replace(/\s+/g, "") !== tvClean && (
                  <div data-testid="drawer-tv-changed" className="mt-1 flex items-center gap-1.5 text-[11px] font-mono text-amber-700">
                    <PlugZap className="w-3 h-3" /> {t("tv_changed")} · {t("tv_prev")}: {device.tv_prev_id || "?"} · {fmtDate(device.tv_changed_at)}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase text-gray-600 mb-1 block">
                  {t("location_name")} <span className="text-gray-600">({t("location_id")}: {device.location_id || "—"})</span>
                </label>
                <input data-testid="drawer-location-input" value={locName} onChange={(e) => setLocName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase text-gray-600 mb-1 block">{t("note")}</label>
                <input data-testid="drawer-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase text-gray-600 mb-1 block">{lang === "de" ? "Schließdatum" : "Closing date"}</label>
                <input data-testid="drawer-closes-input" type="date" value={closesOn} onChange={(e) => setClosesOn(e.target.value)} className={inputCls} />
                {device.moved_from && (
                  <p data-testid="drawer-moved" className="mt-1 text-[11px] text-amber-700 font-mono">{lang === "de" ? "Umgezogen von" : "Moved from"} {device.moved_from}{device.moved_at ? ` · ${fmtDateOnly(device.moved_at)}` : ""}</p>
                )}
              </div>
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input data-testid="drawer-task-flag" type="checkbox" checked={taskFlag} onChange={(e) => setTaskFlag(e.target.checked)} className="accent-amber-500 w-4 h-4" />
                  <span className="inline-flex items-center gap-1.5 text-sm text-amber-200 font-medium"><Flag className="w-3.5 h-3.5" /> {lang === "de" ? "Als offene Aufgabe markieren" : "Flag as open task"}</span>
                </label>
                {taskFlag && (
                  <textarea data-testid="drawer-task-note" value={taskNote} onChange={(e) => setTaskNote(e.target.value)} rows={2}
                    placeholder={lang === "de" ? "Was stimmt nicht? (erscheint im Bericht)" : "What is wrong? (shows in report)"}
                    className={`${inputCls} mt-2`} />
                )}
              </div>
              <div className="flex gap-2">
                <button data-testid="drawer-save" onClick={save} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#c00000] hover:bg-[#b00000] disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors">
                  <Save className="w-4 h-4" /> {t("save")}
                </button>
                {tvClean && (() => {
                  const st = tvStatusOf(device);
                  return (
                  <a href={`teamviewer10://control?device=${tvClean}`}
                    title={st === "attention" ? "nicht im TeamViewer-Konto — manueller Eingriff" : st === "offline" ? t("offline") : st === "unknown" ? "unbekannt (kein Live-Status)" : t("online")}
                    className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm border transition-colors ${
                      st === "attention"
                        ? "bg-amber-500/15 border-amber-500/40 text-amber-700 hover:bg-amber-500/25"
                        : st === "offline"
                        ? "bg-red-500/15 border-red-500/40 text-red-700 hover:bg-red-500/25"
                        : st === "unknown"
                        ? "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                        : "bg-[#c00000]/15 border-[#c00000]/40 text-[#c00000] hover:bg-[#c00000]/25"
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st === "attention" ? "bg-amber-400 pulse-dot" : st === "offline" ? "bg-red-400" : st === "unknown" ? "bg-gray-400" : "bg-emerald-500 pulse-dot"}`} />
                    <PlugZap className="w-4 h-4" />
                  </a>
                  );
                })()}
              </div>
              <div className="border-t border-gray-200 pt-3 space-y-2">
                <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600">{lang === "de" ? "Aktionen" : "Actions"}</div>
                <div className="relative">
                  <div className="flex gap-2">
                    <button data-testid="act-print" onClick={printLabel} className="flex-1 flex items-center justify-center gap-2 text-gray-800 border border-gray-200 bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-2 text-sm transition-colors">
                      <Printer className="w-4 h-4" /> {lang === "de" ? "Etikett drucken" : "Print label"}
                    </button>
                    <button data-testid="act-label-config" onClick={() => setLabelCfgOpen((o) => !o)} title={lang === "de" ? "Etikett-Inhalt wählen" : "Choose label content"}
                      className={`flex items-center justify-center rounded-lg px-3 py-2 border transition-colors ${labelCfgOpen ? "bg-[#c00000]/15 border-[#c00000]/40 text-[#c00000]" : "text-gray-700 border-gray-200 bg-gray-50 hover:bg-gray-100"}`}>
                      <SlidersHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                  {labelCfgOpen && (
                    <div data-testid="label-config-panel" className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5">
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[#c00000]/80 mb-1.5">{lang === "de" ? "Etikett-Inhalt" : "Label content"}</div>
                      {LABEL_FIELD_DEFS.map((f) => (
                        <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none hover:text-gray-900 transition-colors">
                          <input type="checkbox" data-testid={`label-opt-${f.key}`} checked={!!lf[f.key]} onChange={() => toggleLabel(f.key)} className="accent-[#c00000] w-4 h-4" />
                          {lang === "de" ? f.de : f.en}
                        </label>
                      ))}
                      <div className="pt-2.5 mt-1.5 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-mono uppercase tracking-wider text-[#c00000]/80">{lang === "de" ? "Breite" : "Width"}</span>
                          <span className="text-sm font-mono font-semibold text-gray-900" data-testid="label-width-value">{labelW} mm</span>
                        </div>
                        <input
                          type="range" min={30} max={80} step={5} value={labelW}
                          data-testid="label-width-slider"
                          onChange={(e) => changeLabelW(parseInt(e.target.value, 10))}
                          className="w-full accent-[#c00000] cursor-pointer"
                        />
                        <div className="flex gap-1.5 mt-2">
                          {[40, 50, 60].map((w) => (
                            <button key={w} data-testid={`label-width-${w}`} onClick={() => changeLabelW(w)}
                              className={`flex-1 rounded-md px-2 py-1 text-xs font-mono border transition-colors ${labelW === w ? "bg-[#c00000]/15 border-[#c00000]/40 text-[#c00000]" : "text-gray-600 border-gray-200 bg-gray-50 hover:bg-gray-100"}`}>
                              {w} mm
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button data-testid="act-return" onClick={returnToLager} className="w-full flex items-center justify-center gap-2 text-[#c00000] border border-[#c00000]/30 bg-[#c00000]/5 hover:bg-[#c00000]/15 rounded-lg px-4 py-2 text-sm transition-colors">
                  <RotateCcw className="w-4 h-4" /> {lang === "de" ? "Zurückgekommen → prüfen & einlagern" : "Returned → check & stock"}
                </button>
                <button data-testid="act-assign" onClick={assignStation} className="w-full flex items-center justify-center gap-2 text-[#c00000] border border-[#c00000]/30 bg-[#c00000]/5 hover:bg-[#c00000]/15 rounded-lg px-4 py-2 text-sm transition-colors">
                  <ArrowRightLeft className="w-4 h-4" /> {lang === "de" ? "Neuer Station zuweisen" : "Assign to station"}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="act-defekt" onClick={markDefekt} className="flex items-center justify-center gap-2 text-amber-700 border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/15 rounded-lg px-3 py-2 text-sm transition-colors">
                    <Wrench className="w-4 h-4" /> {lang === "de" ? "Defekt" : "Defective"}
                  </button>
                  <button data-testid="act-retire" onClick={markAusgemustert} className="flex items-center justify-center gap-2 text-gray-600 border border-gray-200 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 text-sm transition-colors">
                    <Archive className="w-4 h-4" /> {lang === "de" ? "Ausmustern" : "Retire"}
                  </button>
                </div>
              </div>
              <button data-testid="drawer-delete" onClick={remove}
                className="w-full flex items-center justify-center gap-2 text-red-600 border border-red-500/30 bg-red-500/5 hover:bg-red-500/15 rounded-lg px-4 py-2 text-sm transition-colors">
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
            </div>

            {/* Info tiles column */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card title={lang === "de" ? "Tickets / To-dos" : "Tickets / To-dos"} icon={Ticket} wide testid="card-tickets">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-gray-600">{tickets.length > 0 ? `${tickets.length} ${lang === "de" ? "für dieses Gerät" : "for this device"}` : (lang === "de" ? "Keine Tickets für dieses Gerät" : "No tickets for this device")}</span>
                    <button data-testid="drawer-ticket-new" onClick={() => { setTicketEditing(null); setTicketDlg(true); }} className="flex items-center gap-1.5 bg-amber-500/90 hover:bg-amber-500 text-black font-semibold rounded-lg px-2.5 py-1.5 text-xs transition-colors">
                      <Plus className="w-3.5 h-3.5" /> {lang === "de" ? "Neues Ticket" : "New ticket"}
                    </button>
                  </div>
                  <div className="space-y-2" data-testid="drawer-ticket-list">
                    {[...tickets].sort(sortTickets).map((tk) => (
                      <TicketCard key={tk.id} ticket={tk} users={ticketUsers} onEdit={(t) => { setTicketEditing(t); setTicketDlg(true); }} onChanged={loadTickets} />
                    ))}
                  </div>
                </Card>
                {hasStation && (
                  <Card title={lang === "de" ? "Stationsinformationen" : "Station info"} icon={MapPin} wide testid="card-station">
                    {st.name && <Field label="Stationsname" value={st.name} mono={false} />}
                    <LinkField label="Adresse" value={st.addr} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(st.addr)}`} external testid="station-address-link" />
                    <LinkField label="Telefon" value={st.tel} href={`tel:${st.tel.replace(/[^+\d]/g, "")}`} testid="station-phone-link" />
                    <LinkField label={lang === "de" ? "Tel. intern" : "Phone (int.)"} value={st.telIntern} href={`tel:${st.telIntern.replace(/[^+\d]/g, "")}`} testid="station-phone-int-link" />
                    <LinkField label="E-Mail" value={st.email} href={`mailto:${st.email}`} testid="station-email-link" />
                    {st.manager && <Field label={lang === "de" ? "Stationsleiter" : "Manager"} value={st.manager} mono={false} />}
                    {(st.mofr || st.sa || st.so) && (
                      <div className="py-2 border-b border-gray-100 sm:col-span-2" data-testid="station-hours">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{lang === "de" ? "Öffnungszeiten" : "Opening hours"}</div>
                        <div className="grid grid-cols-3 gap-3 text-sm text-gray-800">
                          <div><span className="text-[10px] font-mono uppercase text-gray-600 block">Mo–Fr</span>{st.mofr || "—"}</div>
                          <div><span className="text-[10px] font-mono uppercase text-gray-600 block">Sa</span>{st.sa || "—"}</div>
                          <div><span className="text-[10px] font-mono uppercase text-gray-600 block">So</span>{st.so || "—"}</div>
                        </div>
                      </div>
                    )}
                    {st.agentur && <Field label={lang === "de" ? "Betriebsart" : "Operation type"} value={st.agentur} mono={false} />}
                    {st.kst && <Field label={lang === "de" ? "Kostenstelle" : "Cost center"} value={st.kst} mono={false} />}
                  </Card>
                )}
                {groups.map((g) => (
                  <Card key={g.id} title={g.label} icon={g.icon} testid={`detail-group-${g.id}`}>
                    {g.keys.map(renderField)}
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Änderungsverlauf: unten, aufklappbar */}
          <div className="mt-5 border-t border-gray-200 pt-4">
            <button data-testid="history-toggle" onClick={() => setHistOpen((o) => !o)}
              className="w-full flex items-center justify-between text-left group">
              <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gray-600 group-hover:text-gray-900 transition-colors">
                <History className="w-3.5 h-3.5" /> {t("change_history")} <span className="text-gray-600">({history.length})</span>
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${histOpen ? "rotate-180" : ""}`} />
            </button>
            {histOpen && (
              history.length === 0 ? (
                <div className="mt-3 text-sm text-gray-600">{t("no_history")}</div>
              ) : (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2" data-testid="history-list">
                  {history.map((h, i) => (
                    <div key={i} className="relative pl-4 border-l border-gray-200 pb-2">
                      <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#c00000]" />
                      <div className="text-xs text-gray-600 font-mono">{fmtDate(h.timestamp)}</div>
                      <div className="text-sm text-gray-800">
                        {h.type === "registered" ? (
                          <span className="text-[#c00000]">{h.message || t("registered")}</span>
                        ) : (
                          <>
                            <span className="font-mono text-[#c00000]">{h.field}</span>{" "}
                            <span className="text-gray-600">{h.old_value || "∅"} → <span className="text-gray-800">{h.new_value}</span></span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
      <TicketDialog
        open={ticketDlg}
        onClose={() => setTicketDlg(false)}
        onSaved={loadTickets}
        initial={ticketEditing || { location_id: device.location_id, device_code: device.device_code }}
        lockLocation={device.location_id}
        devicesByLoc={{ [device.location_id]: [device] }}
        users={ticketUsers}
      />
    </div>
  );
}
